import { Suspense, lazy } from 'react';
import { CircularProgress, Stack } from '@mui/material';

import { BodyText, MinorHeading } from '../../../shared/typography/PrimaryTypography';
import { BoundingBox, EsgRecord } from '../../../../types/syntiro_honey';
import SourceFileLink from '../SourceFileLink';
import { readPageHint, readSlideHint } from '../source_file';
import { resolveSourceFormat } from './source_format';

// Code-split: Univer (with the exceljs parser), pdf.js and the deck reader only
// load when a source of that kind is actually opened — they are far too heavy for
// the eager bundle, and a reviewer opens one format at a time.
const UniverWorkbookPreview = lazy(() => import('../univer/UniverWorkbookPreview'));
const PdfDocumentView = lazy(() => import('./PdfDocumentView'));
const PptxSlideView = lazy(() => import('./PptxSlideView'));
const ImageSourceView = lazy(() => import('./ImageSourceView'));

interface SourceDocumentTabProps {
  /** The record whose source document to show. */
  record: EsgRecord;
  /** The anchored value's rectangle, memoised by the parent; null when unanchored. */
  bbox: BoundingBox | null;
}

/** Reads a non-empty string off the extraction payload, or undefined. */
function payloadText(rawPayload: Record<string, unknown>, key: string): string | undefined {
  const value = rawPayload[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/** Reads a non-Excel locator's in-document hint (`image:2024 bar` → `2024 bar`). */
function imageLocator(rawPayload: Record<string, unknown>): string | undefined {
  const cell = payloadText(rawPayload, 'cell');
  const match = cell === undefined ? null : /image:(.+)$/i.exec(cell);
  return match === null ? undefined : match[1].trim();
}

/**
 * The detail modal's source tab: shows the record's original document, whichever
 * kind it is. A spreadsheet opens in the workbook preview at the cited cell, a PDF
 * on the cited page with the anchored value boxed, a deck on the cited slide, an
 * image whole. Anything else falls back to the download link, so the tab is never
 * a dead end.
 * @returns {JSX.Element}
 */
export default function SourceDocumentTab({ record, bbox }: SourceDocumentTabProps) {
  const format = resolveSourceFormat(record.fileName, record.fileMimeType);
  const sourceQuote = payloadText(record.rawPayload, 'sourceQuote');
  const anchored =
    typeof record.rawPayload.anchored === 'boolean' ? record.rawPayload.anchored : undefined;

  const fallback = (
    <Stack alignItems="center" sx={{ py: 6 }}>
      <CircularProgress />
    </Stack>
  );

  if (format === 'UNSUPPORTED') {
    return (
      <Stack spacing={1.5}>
        <MinorHeading weight="Medium">Source document</MinorHeading>
        <BodyText color="text.secondary">
          This file type cannot be previewed here. Open the original to check the value.
        </BodyText>
        <SourceFileLink record={record} />
      </Stack>
    );
  }

  return (
    <Suspense fallback={fallback}>
      {format === 'SPREADSHEET' && (
        <UniverWorkbookPreview
          fileId={record.fileId}
          fileName={record.fileName}
          sheet={payloadText(record.rawPayload, 'sheet') ?? null}
          cell={payloadText(record.rawPayload, 'cell') ?? null}
          showEmptyState
          height="64vh"
        />
      )}
      {format === 'PDF' && (
        <PdfDocumentView
          fileId={record.fileId}
          bbox={bbox}
          pageHint={readPageHint(record.rawPayload)}
          sourceQuote={sourceQuote}
          anchored={anchored}
        />
      )}
      {format === 'PPTX' && (
        <PptxSlideView
          fileId={record.fileId}
          slideHint={readSlideHint(record.rawPayload)}
          sourceQuote={sourceQuote}
          anchored={anchored}
          locator={imageLocator(record.rawPayload)}
        />
      )}
      {format === 'IMAGE' && (
        <ImageSourceView
          fileId={record.fileId}
          fileName={record.fileName}
          locator={imageLocator(record.rawPayload)}
          sourceQuote={sourceQuote}
        />
      )}
    </Suspense>
  );
}
